import React, { useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  DocumentTextIcon,
  FilmIcon,
  LinkIcon,
  MapIcon,
  MusicalNoteIcon,
  PhotoIcon,
  PlusIcon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CONFIDENTIALITY_LEVELS, DOCUMENT_CATEGORIES, SENSITIVITY_FLAGS } from '../types/caseForm';
import AddDocumentForm from './AddDocumentForm';
import { useSuggestions } from './CaseSuggestions';
import { formatFieldPath, ValidationSummary } from './AiValidationPanel';
import NotDocumented from './NotDocumented';
import { GapRow } from '../utils/documentationGaps';
import { formatCaseDate } from '../utils/formatDateValue';

// "Sources & Documents": a case's sources and documents in one place — a main-column section, the sources full width
// one per line, the documents in a grid under them — with a per-source trace of which fields cite it (from AI auto-fills,
// conflicts, findings and related-source suggestions). Clicking a source opens a slide-over listing
// every snippet recorded against it; external pages are never embedded, since most refuse to be
// framed.

type Origin = 'Auto-filled' | 'Conflict' | 'Finding' | 'Related source' | 'AI search';
interface Citation { origin: Origin; fields: string[]; snippet?: string; note?: string; title?: string }
interface SourceRow { url: string; kind: string; cites: Citation[]; fields: string[] }

const KIND_META: Record<string, { label: string; color: string }> = {
  ORIGIN: { label: 'Original', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  MERGE: { label: 'Follow-up', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  CORROBORATION: { label: 'Corroboration', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  ARCHIVE: { label: 'Archive', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  MANUAL: { label: 'Manual', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  AI: { label: 'AI search', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
};
const ORIGIN_COLOR: Record<Origin, string> = {
  'Auto-filled': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  Conflict: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  Finding: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  'Related source': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  'AI search': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const normUrl = (u: string) => u.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '').toLowerCase();
const hostOf = (u: string) => {
  const h = u.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  return h.length > 60 ? h.slice(0, 60) + '…' : h;
};

// --- Documents: one row per file. The name and Download on the first line; category, author,
// document date, language, format and size on one muted line under it; description and any
// restriction or sensitivity chips below. Codes are shown as the labels the upload form uses.

const byValue = (list: ReadonlyArray<{ value: string; label: string }>) =>
  list.reduce<Record<string, string>>((acc, x) => { acc[x.value] = x.label; return acc; }, {});
const CATEGORY_LABEL = byValue(DOCUMENT_CATEGORIES);
const CONFIDENTIALITY_LABEL = byValue(CONFIDENTIALITY_LEVELS);
// Short chip text from the flag's bracketed prefix ("[GRAPHIC CONTENT]" → "Graphic content"); the
// full sentence goes in the tooltip. Flags not in the list are shown as written, dashes as spaces.
const sensitivityChip = (flag: string): { short: string; long: string } => {
  const f = SENSITIVITY_FLAGS.find((x) => x.value === flag);
  if (!f) return { short: flag.replace(/[-_]+/g, ' '), long: flag };
  const bare = f.prefix.replace(/^\[|\]$/g, '').toLowerCase();
  return { short: bare.charAt(0).toUpperCase() + bare.slice(1), long: f.label };
};

const fileKind = (mime: string | undefined, name: string, category: string | undefined): { label: string; Icon: React.ElementType } => {
  const m = (mime || '').toLowerCase();
  const ext = (name.includes('.') ? name.split('.').pop() || '' : '').toLowerCase();
  const label =
    m === 'application/pdf' || ext === 'pdf' ? 'PDF'
    : m.startsWith('image/') ? 'Image'
    : m.startsWith('video/') ? 'Video'
    : m.startsWith('audio/') ? 'Audio'
    : m.includes('spreadsheet') || m.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext) ? 'Spreadsheet'
    : m.includes('word') || ['doc', 'docx', 'odt', 'rtf'].includes(ext) ? 'Document'
    : m.startsWith('text/') ? 'Text'
    : ext ? ext.toUpperCase() : 'File';
  const Icon: React.ElementType =
    category === 'MAP' ? MapIcon
    : label === 'Image' ? PhotoIcon
    : label === 'Video' ? FilmIcon
    : label === 'Audio' ? MusicalNoteIcon
    : label === 'Spreadsheet' ? TableCellsIcon
    : label === 'PDF' || label === 'Document' || label === 'Text' ? DocumentTextIcon
    : DocumentIcon;
  return { label, Icon };
};
const fileSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
// "es" → "Spanish" where the browser can name it; anything else (free text, unknown code) as typed.
const languageName = (code: string) => {
  try {
    const DN = (Intl as any).DisplayNames;
    if (DN && /^[a-z]{2,3}(-[A-Za-z]{2,4})?$/i.test(code)) {
      const n = new DN(['en'], { type: 'language' }).of(code);
      if (n && n.toLowerCase() !== code.toLowerCase()) return n;
    }
  } catch { /* older browser: show the code */ }
  return code;
};

const DocumentRow: React.FC<{ doc: any; onDownload: (id: string) => void }> = ({ doc, onDownload }) => {
  const kind = fileKind(doc.fileType, doc.fileName || '', doc.category);
  const Icon = kind.Icon;
  const meta = [
    CATEGORY_LABEL[doc.category] ?? doc.category,
    doc.authorSource,
    doc.documentDate ? formatCaseDate(doc.documentDate) : null,
    doc.language ? languageName(String(doc.language)) : null,
    `${kind.label}, ${fileSize(doc.fileSize || 0)}`,
  ].filter(Boolean);
  const warnings: string[] = Array.isArray(doc.sensitivityWarnings) ? doc.sensitivityWarnings : [];
  const confidentiality: string = doc.confidentiality || 'INTERNAL';
  const showConfidentiality = confidentiality !== 'INTERNAL';
  return (
    <li className="py-3 flex items-start gap-3">
      <Icon className="h-5 w-5 mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={doc.uploadedAt ? `${doc.fileName} — uploaded ${formatCaseDate(doc.uploadedAt)}` : doc.fileName}>
            {doc.fileName}
          </p>
          <button
            type="button"
            onClick={() => onDownload(doc.id)}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-800 dark:text-emerald-400"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" /> Download
          </button>
        </div>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{meta.join(' · ')}</p>
        {doc.description && <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{doc.description}</p>}
        {(showConfidentiality || warnings.length > 0) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {showConfidentiality && (
              <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${confidentiality === 'RESTRICTED' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                {CONFIDENTIALITY_LABEL[confidentiality] ?? confidentiality}
              </span>
            )}
            {warnings.map((w) => {
              const c = sensitivityChip(w);
              return (
                <span key={w} title={c.long} className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">{c.short}</span>
              );
            })}
          </div>
        )}
      </div>
    </li>
  );
};

interface Props {
  caseId: string;
  caseData: Record<string, any>;
  documents: any[];
  documentsLoading: boolean;
  onDownload: (documentId: string) => void;
  validation: ValidationSummary | null;
  sourceGaps: GapRow[];
  canEdit: boolean;
  // Mirrors the backend's upload rule (ADMIN, APPROVER, or the submitter while DRAFT/FOR_VALIDATION).
  canUpload: boolean;
  onDocumentsChange: () => void;
  onJumpToField: (path: string) => void;
}

const EvidenceCard: React.FC<Props> = ({ caseId, caseData, documents, documentsLoading, onDownload, validation, sourceGaps, canEdit, canUpload, onDocumentsChange, onJumpToField }) => {
  const { items: suggestionItems } = useSuggestions();
  const [showAll, setShowAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openRow, setOpenRow] = useState<SourceRow | null>(null);
  const [shown, setShown] = useState(false);
  const openSource = (row: SourceRow) => { setOpenRow(row); requestAnimationFrame(() => setShown(true)); };
  const closeSource = () => { setShown(false); setTimeout(() => setOpenRow(null), 300); };
  const jumpFromPanel = (path: string) => { closeSource(); onJumpToField(path); };

  const rows = useMemo<SourceRow[]>(() => {
    const cites = new Map<string, { url: string; items: Citation[] }>();
    const cite = (url: string | null | undefined, c: Citation) => {
      if (!url) return;
      const k = normUrl(url);
      if (!k) return;
      if (!cites.has(k)) cites.set(k, { url, items: [] });
      cites.get(k)!.items.push(c);
    };
    const ai = caseData.metadata?.ai ?? {};
    for (const a of (ai.autoApplied ?? []) as any[]) {
      if (a.undone || a.superseded) continue;
      cite(a.source, { origin: 'Auto-filled', fields: a.field ? [a.field] : [], snippet: a.snippet, note: a.message });
    }
    for (const c of (ai.conflicts ?? []) as any[]) {
      cite(c.source, { origin: 'Conflict', fields: c.field ? [c.field] : [], snippet: c.snippet, note: c.message });
    }
    for (const f of validation?.findings ?? []) {
      cite(f.source, { origin: 'Finding', fields: (f.fixes ?? []).map((x) => x.field), snippet: f.snippet, note: f.message });
    }
    for (const s of validation?.aiSources ?? []) {
      cite(s.url, { origin: 'AI search', fields: [], title: s.title, note: s.type });
    }
    for (const it of suggestionItems) {
      if (it.origin !== 'dedup' || !it.sourceUrl) continue;
      cite(it.sourceUrl, { origin: 'Related source', fields: it.field ? [it.field] : [], snippet: it.snippet, note: it.reason });
    }

    const out: SourceRow[] = [];
    const seen = new Set<string>();
    const push = (url: string, kind: string) => {
      const k = normUrl(url);
      if (!k || seen.has(k)) return;
      seen.add(k);
      const items = cites.get(k)?.items ?? [];
      const fields = Array.from(new Set(items.flatMap((c) => c.fields)));
      out.push({ url, kind, cites: items, fields });
    };
    if (caseData.sourceInformation) push(caseData.sourceInformation, 'ORIGIN');
    for (const s of (caseData.metadata?.additionalSources ?? []) as any[]) {
      if (s?.url) push(s.url, s.kind || 'MANUAL');
    }
    // Sources the AI cited that the case does not record: still evidence, so list them after.
    for (const { url } of Array.from(cites.values())) push(url, 'AI');
    return out;
  }, [caseData, validation, suggestionItems]);

  const visible = showAll ? rows : rows.slice(0, 5);
  const subHeading = (label: string, count: number) => (
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
      {label} <span className="font-normal text-gray-400 dark:text-gray-500">({count})</span>
    </h3>
  );

  return (
    <>
      {/* Same card chrome and heading as the other case sections. Sources take the full width so a
          URL stays on one line; documents sit under them, two cards per row from lg. */}
      <div id="section-evidence" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Sources &amp; Documents</h2>
        <div>
          <section aria-label="Sources">
            <div className="mb-2.5">{subHeading('Sources', rows.length)}</div>
            {rows.length === 0 && sourceGaps.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">No sources recorded</p>
            )}
            <ul className="space-y-2.5">
              {visible.map((row) => {
                const m = KIND_META[row.kind] ?? KIND_META.MANUAL;
                // The original source may be a description rather than a link (a survey, a field
                // visit): then the text wraps and there is nothing to open.
                const isLink = /^https?:\/\//i.test(row.url.trim());
                return (
                  <li key={row.url} className="text-xs">
                    <div className="flex items-start gap-2">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full font-medium shrink-0 ${m.color}`}>{m.label}</span>
                      <button
                        type="button"
                        onClick={() => openSource(row)}
                        title={isLink ? row.url : undefined}
                        className={`flex-1 min-w-0 text-left text-gray-800 dark:text-gray-200 hover:underline py-0.5 ${isLink ? 'truncate' : ''}`}
                      >
                        {isLink ? hostOf(row.url) : row.url}
                      </button>
                      {row.fields.length > 0 && (
                        <span className="shrink-0 py-0.5 text-gray-400 dark:text-gray-500" title="Fields this source supports — click the source for the trace">
                          {row.fields.length} field{row.fields.length === 1 ? '' : 's'}
                        </span>
                      )}
                      {isLink && (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Open original in a new tab"
                          title="Open original"
                          className="shrink-0 py-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <NotDocumented rows={sourceGaps} caseId={caseId} canEdit={canEdit} className={rows.length ? 'mt-3' : 'mt-0'} />
            {rows.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="mt-3 text-xs font-medium text-gray-700 dark:text-gray-300 underline hover:no-underline"
              >
                {showAll ? 'See less' : `See more (${rows.length - 5})`}
              </button>
            )}
          </section>

          <section aria-label="Documents" className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex items-center justify-between gap-2">
              {subHeading('Documents', documents.length)}
              {canUpload && !adding && (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-800 dark:text-emerald-400"
                >
                  <PlusIcon className="h-4 w-4" aria-hidden="true" /> Add document
                </button>
              )}
            </div>
            {canUpload && adding && (
              <AddDocumentForm
                caseId={caseId}
                onUploaded={() => { setAdding(false); onDocumentsChange(); }}
                onCancel={() => setAdding(false)}
              />
            )}
            {documentsLoading ? (
              <div className="flex items-center justify-center py-4 text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                Loading...
              </div>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No documents uploaded</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {documents.map((doc) => <DocumentRow key={doc.id} doc={doc} onDownload={onDownload} />)}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Source slide-over: everything recorded against one URL */}
      {openRow && (
        <div className="fixed inset-0 z-50">
          <div className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${shown ? 'opacity-100' : 'opacity-0'}`} onClick={closeSource} />
          <div className={`fixed inset-y-0 right-0 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /> Source
              </h3>
              <button onClick={closeSource} aria-label="Close" className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" aria-hidden="true" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
              <div>
                <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${(KIND_META[openRow.kind] ?? KIND_META.MANUAL).color}`}>
                  {(KIND_META[openRow.kind] ?? KIND_META.MANUAL).label}
                </span>
                {openRow.cites.find((c) => c.title)?.title && (
                  <p className="mt-2 font-medium text-gray-900 dark:text-gray-100">{openRow.cites.find((c) => c.title)!.title}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-all">{openRow.url}</p>
                <a
                  href={openRow.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Open original <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  What this source supports
                </p>
                {openRow.cites.filter((c) => c.origin !== 'AI search').length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No extracted text is recorded against this source.</p>
                ) : (
                  <div className="space-y-2">
                    {openRow.cites.filter((c) => c.origin !== 'AI search').map((c, i) => (
                      <div key={i} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 text-xs">
                        <div className="flex flex-wrap items-center gap-1 mb-1.5">
                          <span className={`inline-flex px-1.5 py-0.5 rounded-full font-medium ${ORIGIN_COLOR[c.origin]}`}>{c.origin}</span>
                          {c.fields.map((f) => (
                            <button
                              key={f}
                              type="button"
                              onClick={() => jumpFromPanel(f)}
                              title="Show this field on the page"
                              className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                              {formatFieldPath(f, caseData)}
                            </button>
                          ))}
                        </div>
                        {c.snippet && (
                          <blockquote className="italic text-gray-600 dark:text-gray-300 border-l-2 border-gray-300 dark:border-gray-600 pl-2">“{c.snippet}”</blockquote>
                        )}
                        {c.note && <p className="mt-1 text-gray-500 dark:text-gray-400">{c.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EvidenceCard;
